/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：ブラウザをリフレッシュと閉じる時の処理をまとめするサービス
 * 要望：
 * ※リフレッシュするとき、当画面を再表示する
 * ※閉じるとき、ユーザログアウト処理を実行する
 *
 * 技術前提：
 * ブラウザのリフレッシュ処理は「1．閉じる」、「2．再開」です。
 * 「1．閉じる」の処理はブラウザの「閉じる」ボタンと同じですから、
 * ブラウザはリフレッシュと閉じる二つの閉じる処理が区別されない。
 *
 * 実現方式：
 * 1．閉じる時に全部ログアウトする
 * 2．開く時にリフレッシュや閉じる処理を判断する
 * 3.リフレッシュの場合、ログインを実行して、閉じる前の画面へ遷移する
 *
 * ※「No3」を実現するため、ログインしたユーザのIDとパスワードを保存する必要がある
 * ※「No3」を実現するため、閉じる前の画面を記録する必要がある
 *
 * 作成者：2024/12/12 ノア）黄
 * 最終修正者：2024/12/12 ノア）黄
 *
 */
import { clearLocal, clearSession, localService, sessionService } from '@/commons/services/localService'
import config from '@/config/config'
import VueRouter, { Route } from 'vue-router'
import { LoginParam } from '@/models'
import { postLogout, postLogin, getCorporateMasterData } from '@/store/apis'
import { getCookie, removeCookie, setCookie } from '@/utils/cookieUtils'
import { AccessToken, UserRole } from '@/commons/constants'
import HelperService from '@/service/helperService'
import router from '@/router'
import Vue from 'vue'

export const LAST_PATH = 'lastPath'
export const LOGIN_PARAM = 'loginParam'

/**
 * クッキーのクリア
 */
export function clearWindowCookie(): void {
  removeCookie(LAST_PATH)
  removeCookie(LOGIN_PARAM)
}

/**
 * ※「No3」を実現するため、閉じる前の画面を記録する必要がある
 *
 * @param {VueRouter} router router
 * @returns {void}
 */
export function recordCurrentPage(router: VueRouter): void {
  router.afterEach((to: Route, from: Route) => {
    if (to.path === config.menu.home && from.path === config.menu.login) {
      return
    }

    if (to.path !== config.menu.login) {
      setCookie(LAST_PATH, (window.location.pathname + window.location.search).substring(1))
    }
  })
}

/**
 * リフレッシュ処理ですかどうかを判断する
 *
 * @returns {boolean} リフレッシュ処理ですか
 */
export function isRefresh(): boolean {
  return performance.navigation.type === performance.navigation.TYPE_RELOAD
}

/**
 * ログイン情報を保存する
 *
 * @param {LoginParam} loginParam ログイン情報
 * @returns {void}
 */
export function saveLoginParam(loginParam: LoginParam): void {
  setCookie(LOGIN_PARAM, JSON.stringify(loginParam))
}

/**
 * ログアウト処理を実行成功すれば、Localは全部クリアする必要があるので
 * 再開するとき必要な二つパラメータをメモリに一時保存する
 */
const url = config.api.baseUrl + config.api.logout

/**
 * アプリを作成するとき、ブラウザの閉じる事件をリスニング
 * リフレッシュの場合、「No3」を実行する
 *
 * @returns {void}
 */
export function onAppMounted(): void {
  window.addEventListener('beforeunload', beforeUnloadHandler)
  window.addEventListener('unload', unloadHandler)
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
  if (navigation && navigation.type === 'reload') {
    const loginParam = getCookie(LOGIN_PARAM) ? JSON.parse(getCookie(LOGIN_PARAM) as string) : null
    if (loginParam) {
      doPostLogin(loginParam)
    }
  }
}

/**
 * ブラウザの閉じる事件のリスニングを取り消す
 *
 * @returns {void}
 */
export function onAppDestroyed(): void {
  window.removeEventListener('beforeunload', beforeUnloadHandler)
  window.removeEventListener('unload', unloadHandler)
}

/**
 * アンインストール前の処理
 *
 * @returns {void}
 */
function beforeUnloadHandler(): void {
  doLogout()
}

/**
 * アンインストールの処理
 *
 * @returns {void}
 */
function unloadHandler(): void {
  doLogout()
}

/**
 * ブラウザのタイプを判断する
 *
 * @returns {boolean} Chromeかどうか
 */
function isChrome(): boolean {
  const userAgent = navigator.userAgent.toLowerCase()
  return userAgent.includes('chrome') || userAgent.includes('edge') || userAgent.includes('edg')
}

/**
 * ログアウトを実行する。
 *
 * @returns {void}
 */
function doLogout(): void {
  // 閉じる時、ブラウザは速めに処理を実行するために、async処理を中止する可能性があるので、
  // 二つ種類のログアウト処理を実行する

  postLogout()
  xhrLogout()
  clearLocal()
  clearSession()
  HelperService.removeAccessToken()
}

/**
 * XMLHttpRequestでログアウトを実行する。
 *
 * @returns {void}
 */
function xhrLogout(): void {
  const token = getCookie(AccessToken)
  const xhr = new XMLHttpRequest()
  xhr.open('POST', url, isChrome() ? true : false)
  xhr.setRequestHeader('authorization', token)
  xhr.send()
}

/**
 * ログイン
 *
 * @param {LoginParam} loginParam ログインのパラメータ
 * @returns {Promise<void>} アクセストークン
 */
async function doPostLogin(loginParam: LoginParam): Promise<void> {
  const data = await postLogin(loginParam)
  if (!data) {
    return
  }
  setToken(data)

  if (!localService.userInfo.userId.startsWith('mgr') && localService.userInfo.role === UserRole.SYSTEM) {
    localService.itpConnectValidFlag = (await getCorporateMasterData()).itpConnectValidFlag
  }

  Vue.nextTick(() => {
    const lastPath = getCookie(LAST_PATH)
    router.push(lastPath ? lastPath : '/')
  })
}

/**
 * アクセストークンをローカルストレージに保存する
 *
 * @param {any} data ログインのレスポンス定義
 * @returns {void}
 */
// eslint-disable-next-line
function setToken(data: any): void {
  const jwt = data.token
  const payloadBase64 = jwt.substring(jwt.indexOf('.') + 1, jwt.lastIndexOf('.'))
  const payloadStr = decodeURIComponent(escape(window.atob(payloadBase64)))
  const payload = JSON.parse(payloadStr)
  const userInfo = JSON.parse(payload.userInfo)
  for (const key in data) {
    if (key !== 'token') {
      userInfo[key] = data[key]
    }
  }

  setCookie(AccessToken, jwt)
  localService.userInfo = userInfo
  localService.loginCheck = true
  sessionService.loginCheck = true
}
